import { motion } from "framer-motion";
import { B, PILLARS } from "../data/pillars";
import SectionHeader from "./SectionHeader";

const LEVEL_LABELS = ["Reactive", "Emerging", "Scaling", "Proactive", "Optimized"];
const LEVEL_COLORS = [B.red, B.orange, B.yellow, B.green, B.teal];

export default function ScoreHeatmap({ answers, pillarScores, confidenceMap }) {
  return (
    <div style={{
      background: "#1A2E4A", borderRadius: 16, border: "1px solid #1E3A5F",
      padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box",
    }}>
      <SectionHeader num={2} title="Score Heatmap" sub="All 15 Questions Visualized by Maturity Level" />

      <p style={{ color: B.lightGray, fontSize: 13, lineHeight: 1.7, marginBottom: 24 }}>
        Each cell represents one assessment question. Color indicates OML level achieved.
        Dark cells require the most immediate attention.
      </p>

      {/* Legend */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {LEVEL_LABELS.map((l, i) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{
              width: 14, height: 14, borderRadius: 3,
              background: LEVEL_COLORS[i], flexShrink: 0,
            }} />
            <span style={{ fontSize: 11, color: B.subText, fontWeight: 600 }}>OML {i + 1} – {l}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 8 }}>
          <div style={{
            width: 14, height: 14, borderRadius: 3,
            border: `2px dashed ${B.yellow}`, flexShrink: 0,
          }} />
          <span style={{ fontSize: 11, color: B.yellow, fontWeight: 600 }}>⚠ Needs Validation</span>
        </div>
      </div>

      {/* Heatmap grid */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {PILLARS.map(pillar => {
          const pillarScore = pillarScores[pillar.id];
          return (
            <div key={pillar.id}>
              {/* Pillar header */}
              <div style={{
                display: "flex", alignItems: "center", gap: 10, marginBottom: 8,
              }}>
                <span style={{ fontSize: 16 }}>{pillar.icon}</span>
                <span style={{ color: pillar.color, fontSize: 13, fontWeight: 800 }}>{pillar.label}</span>
                <span style={{ color: B.subText, fontSize: 11 }}>· {pillar.sub}</span>
                <div style={{ flex: 1, height: 1, background: "#1E3A5F" }} />
                <span style={{
                  background: pillar.color + "20", color: pillar.color,
                  fontSize: 11, fontWeight: 800, padding: "2px 10px", borderRadius: 6,
                }}>
                  Avg {pillarScore.toFixed(1)}
                </span>
              </div>

              {/* Question cells */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {pillar.questions.map(q => {
                  const score = answers[q.id] !== undefined ? answers[q.id] + 1 : 1;
                  const isUnsure = confidenceMap[q.id] === false;
                  const c = LEVEL_COLORS[score - 1];
                  return (
                    <motion.div
                      key={q.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.05 }}
                      style={{
                        flex: "1 1 160px",
                        background: c + "18",
                        border: `2px solid ${isUnsure ? B.yellow : c + "60"}`,
                        borderStyle: isUnsure ? "dashed" : "solid",
                        borderRadius: 10,
                        padding: "12px 14px",
                        position: "relative",
                        cursor: "default",
                      }}
                    >
                      {isUnsure && (
                        <div style={{
                          position: "absolute", top: 6, right: 8,
                          fontSize: 12, color: B.yellow,
                        }}>⚠</div>
                      )}
                      <div style={{ fontSize: 10, color: c, fontWeight: 800, letterSpacing: 1, marginBottom: 4 }}>
                        OML {score} · {LEVEL_LABELS[score - 1]}
                      </div>
                      <div style={{ fontSize: 12, color: B.white, fontWeight: 600, lineHeight: 1.4 }}>
                        {q.label}
                      </div>
                      {/* Mini bar */}
                      <div style={{
                        marginTop: 8, background: "#1E3A5F",
                        borderRadius: 3, height: 4, overflow: "hidden",
                      }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: (score / 5 * 100) + "%" }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          style={{ height: "100%", background: c, borderRadius: 3 }}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary stats */}
      <div style={{
        display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap",
      }}>
        {LEVEL_LABELS.map((l, i) => {
          const count = PILLARS.flatMap(p => p.questions).filter(q => {
            const s = answers[q.id] !== undefined ? answers[q.id] + 1 : 1;
            return s === i + 1;
          }).length;
          if (count === 0) return null;
          return (
            <div key={l} style={{
              flex: "1 1 80px", textAlign: "center",
              background: LEVEL_COLORS[i] + "12",
              border: `1px solid ${LEVEL_COLORS[i]}30`,
              borderRadius: 8, padding: "10px",
            }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: LEVEL_COLORS[i] }}>{count}</div>
              <div style={{ fontSize: 10, color: B.subText, fontWeight: 600 }}>OML {i + 1}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}